import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import SmartBackButton from "../components/ui/SmartBackButton";

const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const navigate = useNavigate();
  const { updatePassword, signIn, user } = useAuth();

  const validateForm = () => {
    const errors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };
    let isValid = true;

    // Current password validation
    if (!currentPassword) {
      errors.currentPassword = "Current password is required";
      isValid = false;
    }

    // New password validation
    if (!newPassword) {
      errors.newPassword = "New password is required";
      isValid = false;
    } else if (newPassword.length < 8) {
      errors.newPassword = "New password must be at least 8 characters";
      isValid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      errors.newPassword =
        "Password must contain uppercase, lowercase, and number";
      isValid = false;
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    // Check if new password is different from current
    if (currentPassword === newPassword) {
      errors.newPassword =
        "New password must be different from current password";
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First verify the current password by attempting to sign in
      if (!user?.email) {
        throw new Error("User email not found");
      }

      const { error: signInError } = await signIn(user.email, currentPassword);
      if (signInError) {
        setFieldErrors({
          ...fieldErrors,
          currentPassword: "Current password is incorrect",
        });
        return;
      }

      // Update the password
      const { error: updateError } = await updatePassword(newPassword);
      if (updateError) {
        throw updateError;
      }

      setSuccess(true);

      // Clear form after successful update
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (err: any) {
      setError(err.message || "An error occurred while updating your password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    // Clear field-specific error when user starts typing
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      setFieldErrors({
        ...fieldErrors,
        [field]: "",
      });
    }

    // Clear general error
    if (error) {
      setError(null);
    }

    // Update field value
    switch (field) {
      case "currentPassword":
        setCurrentPassword(value);
        break;
      case "newPassword":
        setNewPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-light flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-primary-200">
            <div className="text-center">
              <CheckCircle className="mx-auto h-12 w-12 text-success-500" />
              <h2 className="mt-4 text-xl font-medium text-neutral-900">
                Password Updated Successfully
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                Your password has been updated. You will be redirected to the
                dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <SmartBackButton fallbackPath="/dashboard" />
        <h1 className="text-2xl font-bold text-neutral-900 mt-4">
          Change Password
        </h1>
        <p className="text-neutral-600 mt-2">
          Update your password to keep your account secure.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        {error && (
          <div className="mb-6 border border-error-500 rounded-md p-3 flex items-start">
            <AlertCircle className="h-5 w-5 text-error-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-error-700">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className="label">
              Current Password
            </label>
            <div className="relative mt-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                id="currentPassword"
                name="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                required
                value={currentPassword}
                onChange={(e) =>
                  handleFieldChange("currentPassword", e.target.value)
                }
                className={`input pl-10 pr-10 ${
                  fieldErrors.currentPassword
                    ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                    : ""
                }`}
                placeholder="Enter your current password"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="text-neutral-400 hover:text-neutral-500"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {fieldErrors.currentPassword && (
              <p className="mt-1 text-sm text-error-600">
                {fieldErrors.currentPassword}
              </p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="label">
              New Password
            </label>
            <div className="relative mt-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                required
                value={newPassword}
                onChange={(e) =>
                  handleFieldChange("newPassword", e.target.value)
                }
                className={`input pl-10 pr-10 ${
                  fieldErrors.newPassword
                    ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                    : ""
                }`}
                placeholder="Enter your new password"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="text-neutral-400 hover:text-neutral-500"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {fieldErrors.newPassword && (
              <p className="mt-1 text-sm text-error-600">
                {fieldErrors.newPassword}
              </p>
            )}
            <div className="mt-2 text-sm text-neutral-500">
              Password must be at least 8 characters and contain uppercase,
              lowercase, and numbers.
            </div>
          </div>

          {/* Confirm New Password */}
          <div>
            <label htmlFor="confirmPassword" className="label">
              Confirm New Password
            </label>
            <div className="relative mt-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) =>
                  handleFieldChange("confirmPassword", e.target.value)
                }
                className={`input pl-10 pr-10 ${
                  fieldErrors.confirmPassword
                    ? "border-error-500 focus:border-error-500 focus:ring-error-500"
                    : ""
                }`}
                placeholder="Confirm your new password"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-neutral-400 hover:text-neutral-500"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {fieldErrors.confirmPassword && (
              <p className="mt-1 text-sm text-error-600">
                {fieldErrors.confirmPassword}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`btn btn-primary ${
                isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <>
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
                  Updating...
                </>
              ) : (
                "Update Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
